import { useLazyQuery } from '@apollo/client';
import { ALL_MODULES, getTranslatedText } from '@nile/components';
import { GET_FIELD, AccountClient } from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import { Button, ButtonGroup } from '@shopify/polaris';
import { Popover, TextStyle, Tippy } from '@superops/ui-design-system';
import {
  DropdownIndicator,
  formatGroupLabel,
  formatOptionLabel,
  IndicatorSeparator
} from '@nile/components';
import { Edit } from '@styled-icons/material/Edit';
import { isEmpty, isEqual } from 'lodash';
import Select from 'react-select';
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  reactSelectErrorStyles,
  reactSelectInlineError
} from '@nile/banyan/src/Utils/style';
import { getCellValue } from '@nile/reports/src/modules/EfficiencyReport/util';
import { useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';

const CustomField = styled.div`
  min-width: 200px;
  max-width: 250px;
  flex-grow: 1;
  padding-right: 10px;
  padding-left: 10px;
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;
const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;
const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;
const EditIcon = styled(Edit)`
  height: 16px;
  width: 16px;
  margin-left: 6px;
  margin-right: 6px;
`;

const CField = styled.div`
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;
function AsyncSelectV2(props) {
  const {
    field,
    ticket,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    isMulti,
    disableSelection,
    isMandatory,
    parentField,
    isClearable
  } = props;
  const intl = useIntl();
  const query = props?.query || GET_FIELD;
  const client = props?.client || AccountClient;
  const variables = props?.variables || {
    input: {
      columnName: field.columnName || field.name,
      module: props.module || ALL_MODULES.ticket
    }
  };
  const [options, setOptions] = useState([]);
  const [getFieldData, { called, loading, data }] = useLazyQuery(query, {
    client: client,
    fetchPolicy: 'cache-and-network',
    variables: variables,
    onCompleted(res) {
      if (res && props?.generateOptions) {
        const generatedOptions = props?.generateOptions(res);
        setOptions(generatedOptions);
        return;
      }
      if (res?.getField) {
        const getField = res?.getField;
        let fieldOpts = getField?.options || getField?.fieldOptions || [];
        if (field?.isDependent) {
          const parentValue =
            getCellValue(
              ticket,
              parentField?.isCustom
                ? `customFields.${field?.nestedTo}`
                : field?.nestedTo
            ) || null;
          const prntValue =
            typeof parentValue === 'object'
              ? parentValue?.name || parentValue?.id
              : parentValue;
          if (prntValue) {
            fieldOpts = fieldOpts?.filter(
              (el) => el?.parentOption?.value === prntValue
            );
          } else {
            fieldOpts = [];
          }
        }
        const customOptions = fieldOpts?.map((el) => {
          return {
            name: el?.value,
            value: el?.value,
            label: el?.value,
            isOptionDisabled: el?.isOptionDisabled
          };
        });
        setOptions(customOptions);
      }
    }
  });
  const updateSelectedField = (val) => {
    if (!val) {
      updateSelection(null);
    }
    updateSelection(val);
  };
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    getFieldData();
    setPopoverActive(!popoverActive);
  });
  const [updatedValue, setUpdatedValue] = useState(value);
  useEffect(() => {
    if (!isEqual(updatedValue, value)) {
      setUpdatedValue(value);
    }
  }, [value]);
  const updateUpdatedValue = (val) => {
    if (!val) {
      setUpdatedValue(null);
    }
    setUpdatedValue(val);
  };
  const displayVal = value
    ? isMulti
      ? value.map((el) => el.label).join(', ')
      : value.label
    : '';

  // const activator = <EditIcon onClick={() => togglePopoverActive()} />;
  const activator = (
    <span
      onClick={() => togglePopoverActive()}
      className="pointer d-in-f actr ml-5 mr-5 mt-5"
    >
      <SVGIcon name="Edit" />
    </span>
  );

  const getTag = () => {
    return (
      <CustomField className="cf-wrapper">
        <Label className="cf-label">
          {field.label || ''}{' '}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        <div className="flex col" key={field.name}>
          <Select
            isLoading={loading}
            className="propSelector"
            key={defaultValue}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            defaultValue={updatedValue}
            components={{ IndicatorSeparator, DropdownIndicator }}
            onChange={updateUpdatedValue}
            options={options}
            placeholder="Choose value"
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              e.stopPropagation();
              e.preventDefault();
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            noOptionsMessage={() => {
              return loading ? 'Loading...' : 'No options';
            }}
            isMulti={isMulti}
            isDisabled={disableSelection}
            styles={
              props.error
                ? {
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    ...reactSelectErrorStyles
                  }
                : {
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }
            }
            isClearable={isClearable}
          />

          {props.error &&
            props.showErrorMessage &&
            reactSelectInlineError(props.error)}
        </div>
        <ButtonGroup className="mt10">
          <Button
            onClick={() => {
              togglePopoverActive();
              setUpdatedValue(value);
            }}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={() => {
              updateSelectedField(updatedValue);
              togglePopoverActive();
            }}
          >
            Save
          </Button>
        </ButtonGroup>
      </CustomField>
    );
  };
  if (inlineEdit) {
    return !popoverActive ? (
      <CField className="fw">
        <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
          <Tippy
            title={
              !editable && parentField
                ? getTranslatedText(
                    intl,
                    'common.ticket.field.dependant.title',
                    {
                      name: parentField?.label
                    }
                  )
                : undefined
            }
            duration={250}
            delay={10}
            placement="bottom-start"
            arrow={false}
            theme="dark"
            appendTo={document.body}
            className="ellipsis"
          >
            <div className="ellipsis" title={displayVal}>
              {displayVal || <DisabledTag>NA</DisabledTag>}
            </div>
          </Tippy>
          {editable && <div className="edit-actions">{activator}</div>}
        </Wrap>
      </CField>
    ) : (
      <CField className="fw">
        <Wrap className="editable-cell">
          <div className="ellipsis" title={displayVal}>
            {displayVal || <DisabledTag>NA</DisabledTag>}
          </div>
          <Popover
            activator={activator}
            active={popoverActive}
            onClose={() => {
              // togglePopoverActive()
            }}
          >
            {getTag()}
          </Popover>
        </Wrap>
      </CField>
    );
  }
  return (
    <CustomField className="cf-wrapper">
      <Label className="cf-label">
        {field.label || ''}
        {isMandatory && (
          <>
            <span className="mandatory-star">*</span>
          </>
        )}
      </Label>
      <div className="flex col" key={field.name}>
        <Select
          className="propSelector"
          key={defaultValue}
          classNamePrefix="value_selector"
          formatGroupLabel={formatGroupLabel}
          formatOptionLabel={formatOptionLabel}
          defaultValue={value}
          components={{ IndicatorSeparator, DropdownIndicator }}
          onChange={updateSelectedField}
          options={options}
          placeholder="Choose value"
          menuPortalTarget={document.body}
          menuPlacement="auto"
          closeMenuOnScroll={(e) => {
            e.stopPropagation();
            e.preventDefault();
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          styles={
            props.error
              ? {
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  }),
                  ...reactSelectErrorStyles
                }
              : {
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }
          }
          noOptionsMessage={() => {
            return loading ? 'Loading...' : 'No options';
          }}
          isMulti={isMulti}
          isDisabled={disableSelection}
        />
        {props.error &&
          props.showErrorMessage &&
          reactSelectInlineError(props.error)}
      </div>
    </CustomField>
  );
}
export default AsyncSelectV2;
